"use client"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import Link from "next/link"
import Image from "next/image"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { cmsLocale, type CmsFaqCategory, type FaqItem } from "@/lib/cms"
import { EditableField } from "@/components/editor/editable-field"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"
import { blobMediaUrl } from "@/lib/blob-url"

interface FaqCategoryClientProps {
  category: CmsFaqCategory
  cmsFaqs: FaqItem[]
  allCategories: CmsFaqCategory[]
}

export function FaqCategoryClient({
  category,
  cmsFaqs,
  allCategories,
}: FaqCategoryClientProps) {
  const ut = useTranslations("ui")
  const lp = useLocalePath()
  const country = useCountry().code
  const categoryName = category.name

  // CMS locale these docs were fetched in — the inline editor stamps it so a
  // draft write targets the matching Payload locale row. No-op in public prod.
  const editLocale = cmsLocale(useLocale(), country)

  const displayFaqs = cmsFaqs.map((f) => ({
    id: f.id,
    slug: f.slug,
    question: f.question,
  }))

  return (
    <>
      {/* Hero */}
      <section className="px-6 pt-12 pb-12 text-center md:pt-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-medium tracking-wider text-neutral-400 uppercase">
            FAQ
          </p>
          <EditableField
            collection="faq-categories"
            id={String(category.id)}
            field="name"
            locale={editLocale}
            kind="scalar"
            as="h1"
            className="text-aubergine-900 mt-4 text-4xl font-normal md:text-5xl"
          >
            {categoryName}
          </EditableField>
          {category.description && (
            <EditableField
              collection="faq-categories"
              id={String(category.id)}
              field="description"
              locale={editLocale}
              kind="scalar"
              as="p"
              className="mx-auto mt-4 max-w-xl text-base text-neutral-500"
            >
              {category.description}
            </EditableField>
          )}
        </div>
      </section>

      {/* FAQ list + sidebar */}
      <section className="px-6 pb-20">
        <div className="mx-auto max-w-5xl">
          <div>
            <div className="flex flex-col gap-10 md:flex-row">
              {/* Sidebar — category nav */}
              <nav className="flex-shrink-0 md:w-56">
                <ul className="flex flex-wrap gap-2 md:flex-col">
                  {allCategories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        href={lp(`/faq/category/${cat.slug}`)}
                        className={cn(
                          "block w-full rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-all",
                          cat.slug === category.slug
                            ? "bg-aubergine-900 text-white"
                            : "hover:text-aubergine-900 text-neutral-500 hover:bg-neutral-100"
                        )}
                      >
                        <EditableField
                          collection="faq-categories"
                          id={String(cat.id)}
                          field="name"
                          locale={editLocale}
                          kind="scalar"
                          as="span"
                        >
                          {cat.name}
                        </EditableField>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Question links */}
              <div className="flex-1 space-y-2">
                {displayFaqs.map((faq) => (
                  <div key={faq.slug}>
                    <Link
                      href={lp(`/faq/${faq.slug}`)}
                      className="group flex items-center justify-between rounded-xl bg-white px-6 py-5 transition-shadow hover:shadow-sm"
                    >
                      <EditableField
                        collection="faq"
                        id={faq.id}
                        field="question"
                        locale={editLocale}
                        kind="scalar"
                        as="span"
                        className="text-aubergine-900 group-hover:text-aubergine-700 pr-4 text-base font-medium transition-colors"
                      >
                        {faq.question}
                      </EditableField>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className="group-hover:text-aubergine-600 flex-shrink-0 text-neutral-400 transition-colors"
                      >
                        <path
                          d="M6 4l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Back link */}
          <div className="mt-8">
            <Link
              href={lp("/faq")}
              className="text-aubergine-700 hover:text-aubergine-900 flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {ut("backToAllFaqs")}
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative overflow-hidden px-6 py-20">
        <Image
          src={blobMediaUrl("/images/misc/advisory-bg.png")}
          alt=""
          fill
          className="absolute inset-0 object-cover"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-200/50 via-transparent to-neutral-200/50" />
        <div className="relative z-10 mx-auto max-w-lg">
          <div>
            <div className="rounded-[16px] bg-white/90 p-8 text-center shadow-lg backdrop-blur">
              <div className="flex justify-center -space-x-2">
                <Image
                  src={blobMediaUrl("/images/avatars/ferdinand.png")}
                  alt=""
                  width={40}
                  height={40}
                  className="rounded-full border-2 border-white object-cover"
                />
                <Image
                  src={blobMediaUrl("/images/avatars/dennis.png")}
                  alt=""
                  width={40}
                  height={40}
                  className="rounded-full border-2 border-white object-cover"
                />
                <Image
                  src={blobMediaUrl("/images/avatars/laura.png")}
                  alt=""
                  width={40}
                  height={40}
                  className="rounded-full border-2 border-white object-cover"
                />
              </div>
              <h3 className="text-aubergine-900 mt-4 text-2xl font-bold">
                {ut("stillHaveQuestions")}
              </h3>
              <p className="mt-2 text-sm text-neutral-600">
                {ut("teamHereToHelp")}{" "}
                <Link href={lp("/contact")} className="underline">
                  {ut("sendUsMessage")}
                </Link>
                .
              </p>
              <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                {/* Discovery-call page is gated out of NL during the waitlist phase. */}
                {isAllowedInCountry("page:free-call", country) && (
                  <Link href={lp("/free-call")} className={buttonVariants()}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="mr-1"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
                    </svg>
                    {ut("bookAdvisoryCall")}
                  </Link>
                )}
                <Link
                  href={lp("/pricing")}
                  className={buttonVariants({ variant: "outline" })}
                >
                  {ut("seePricing")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
